// =============================================================================
// src/lib/webrtc.ts  –  WebRTC file transfer engine
//
//  BUGS FIXED THIS PASS:
//   B1  _pumpChunks concurrent invocation → duplicate chunks → corrupt file
//       Added SendState.pumping flag; guards all entry points
//   B2  TURN servers captured at engine construction before creds fetched
//       Engine now receives a live iceServers getter (fn) evaluated per-RTCPeerConnection
//   B3  RESUME reused broken RTCPeerConnection — destroys old peer first
//   B10 Sends after dc.close() — added dc.readyState guard before dc.send()
// =============================================================================
import type { SignallingClient } from './signalling';
import { logDebug } from './debug';

const CHUNK_SIZE     = 64  * 1024;        // 64 KB per chunk
const LOW_WATERMARK  = 512 * 1024;        // resume sending below 512 KB buffered
const HIGH_WATERMARK = 4   * 1024 * 1024; // pause sending above 4 MB buffered
const MAX_HASH_SIZE  = 100 * 1024 * 1024; // SHA-256 only for files ≤ 100 MB

// ── Types ─────────────────────────────────────────────────────────────────────
export interface TransferProgress {
  transferId: string;
  role:       'sender' | 'receiver';
  bytesDone:  number;
  totalBytes: number;
  speedBps:   number;
  etaSec:     number;
  pct:        number;
}

export interface IncomingFile {
  transferId: string;
  fromPeerId: string;
  name:       string;
  size:       number;
  mimeType:   string;
  sha256:     string;
}

export type ProgressCb = (p: TransferProgress)                     => void;
export type CompleteCb = (transferId: string, role: 'sender' | 'receiver') => void;
export type IncomingCb = (f: IncomingFile)                          => void;
export type ErrorCb    = (transferId: string, code: string)         => void;

// B2: iceServers is a live getter so each RTCPeerConnection gets fresh creds
export type IceServersGetter = () => RTCIceServer[];

interface PeerState {
  peerId: string;
  pc:     RTCPeerConnection;
  iceBuf: RTCIceCandidateInit[];
}

interface SendState {
  peerId:        string;
  file:          File;
  transferId:    string;
  sha256:        string;               // resolved value once sha256Promise settles
  sha256Promise: Promise<string>;      // concurrent — awaited only at TRANSFER_COMPLETE
  dc:            RTCDataChannel | null;
  paused:        boolean;
  cancelled:     boolean;
  pumping:       boolean;              // B1: prevents concurrent _pumpChunks
  bytesSent:     number;
  startTs:       number;
  lastTs:        number;
  lastBytes:     number;
}

interface RecvState {
  fromPeerId:     string;
  transferId:     string;
  name:           string;
  mimeType:       string;
  sha256:         string;
  totalBytes:     number;
  chunks:         Uint8Array[];
  received:       number;
  startTs:        number;
  lastProgressTs: number;
}

// ── SHA-256 helpers ───────────────────────────────────────────────────────────
async function computeSHA256(file: File): Promise<string> {
  if (file.size > MAX_HASH_SIZE) return 'SKIP';
  try {
    const buf  = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return bufToHex(hash);
  } catch { return 'SKIP'; }
}

async function verifySHA256(chunks: Uint8Array[], expected: string): Promise<boolean> {
  if (!expected || expected === '' || expected === 'SKIP') return true;
  try {
    const totalLen = chunks.reduce((s, c) => s + c.byteLength, 0);
    const combined = new Uint8Array(totalLen);
    let off = 0;
    for (const c of chunks) { combined.set(c, off); off += c.byteLength; }
    return bufToHex(await crypto.subtle.digest('SHA-256', combined.buffer)) === expected;
  } catch { return true; }
}

function bufToHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, '0')).join('');
}

// =============================================================================
export class WebRTCEngine {
  private peers     = new Map<string, PeerState>();
  private sending   = new Map<string, SendState>();
  private receiving = new Map<string, RecvState>();

  constructor(
    // B2: Accept a getter instead of a snapshot so each new PeerConnection
    //     gets whatever ICE servers are currently configured (incl. TURN creds)
    private getIceServers: IceServersGetter,
    private signal:        SignallingClient,
    private myPeerId:      string,
    private onProgress:    ProgressCb,
    private onComplete:    CompleteCb,
    private onIncoming:    IncomingCb,
    private onError:       ErrorCb,
  ) {
    this._bindSignalHandlers();
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  /**
   * FIXED: Send FILE_MANIFEST immediately so receiver sees the accept dialog
   * at once. SHA-256 is computed concurrently; before this fix the sender
   * awaited computeSHA256 (could take seconds for large files) before sending
   * the manifest, leaving the receiver with no dialog while the sender already
   * showed "waiting for accept".
   */
  sendFile(toPeerId: string, transferId: string, file: File): void {
    logDebug('webrtc', 'sendFile.start', {
      toPeerId,
      transferId,
      fileName: file.name,
      size: file.size,
      type: file.type,
    });
    // Start SHA-256 in background — never blocks the manifest being sent
    const sha256Promise = computeSHA256(file);

    // Send manifest IMMEDIATELY — receiver shows accept dialog right away
    this.signal.send({
      type:    'FILE_MANIFEST',
      to:      toPeerId,
      payload: {
        transferId,
        name:       file.name,
        size:       file.size,
        sha256:     '',   // real hash sent in TRANSFER_COMPLETE after computation
        chunkCount: Math.ceil(file.size / CHUNK_SIZE),
        chunkSize:  CHUNK_SIZE,
        mimeType:   file.type || 'application/octet-stream',
        isFolder:   false,
      },
    });

    const state: SendState = {
      peerId: toPeerId, file, transferId,
      sha256: '',        // filled when promise resolves
      sha256Promise,
      dc:        null,
      paused:    false,
      cancelled: false,
      pumping:   false,
      bytesSent: 0,
      startTs:   Date.now(),
      lastTs:    Date.now(),
      lastBytes: 0,
    };
    this.sending.set(transferId, state);

    // Update sha256 on the state object once ready (fire-and-forget)
    sha256Promise.then(hash => {
      const s = this.sending.get(transferId);
      if (s) s.sha256 = hash;
    }).catch(() => { /* computeSHA256 never throws, returns 'SKIP' on error */ });
  }

  pauseTransfer(transferId: string): void {
    const s = this.sending.get(transferId);
    if (s) s.paused = true;
  }

  resumeTransfer(transferId: string): void {
    const s = this.sending.get(transferId);
    if (s && s.dc && s.paused) {
      s.paused = false;
      this._pumpChunks(transferId);
    }
  }

  cancelTransfer(transferId: string): void {
    const s = this.sending.get(transferId);
    if (s) {
      s.cancelled = true;
      s.dc?.close();
      this.sending.delete(transferId);
    }
    this.receiving.delete(transferId);
    this.signal.send({ type: 'TRANSFER_CANCEL', payload: { transferId } });
  }

  acceptTransfer(transferId: string): void {
    const r = this.receiving.get(transferId);
    if (!r) return;
    this.signal.send({ type: 'TRANSFER_ACCEPT', to: r.fromPeerId, payload: { transferId } });
  }

  declineTransfer(transferId: string): void {
    const r = this.receiving.get(transferId);
    if (!r) return;
    this.signal.send({ type: 'TRANSFER_DECLINE', to: r.fromPeerId, payload: { transferId, reason: 'User declined' } });
    this.receiving.delete(transferId);
  }

  checkAndResumePendingReceives(peerId: string): void {
    for (const [transferId, r] of this.receiving) {
      if (r.fromPeerId === peerId && r.received > 0 && r.received < r.totalBytes) {
        this.signal.send({ type: 'RESUME', to: peerId, payload: { transferId, lastAck: r.received } });
      }
    }
  }

  destroyPeer(peerId: string): void {
    const p = this.peers.get(peerId);
    if (!p) return;
    p.pc.close();
    this.peers.delete(peerId);
  }

  destroyAll(): void {
    this.peers.forEach(p => p.pc.close());
    this.peers.clear();
    this.sending.clear();
    this.receiving.clear();
  }

  // ── Signalling handlers ─────────────────────────────────────────────────────

  private _bindSignalHandlers(): void {

    this.signal.on('SDP_OFFER', async (msg) => {
      const { sdp, transferId } = msg.payload as { sdp: string; transferId?: string };
      const fromPeerId = msg.from!;
      const peer = this._getOrCreatePeer(fromPeerId);
      await peer.pc.setRemoteDescription({ type: 'offer', sdp });
      this._flushIceBuf(peer);
      const answer = await peer.pc.createAnswer();
      await peer.pc.setLocalDescription(answer);
      this.signal.send({ type: 'SDP_ANSWER', to: fromPeerId, payload: { sdp: answer.sdp!, transferId } });
    });

    this.signal.on('SDP_ANSWER', async (msg) => {
      const { sdp } = msg.payload as { sdp: string };
      const peer = this.peers.get(msg.from!);
      if (!peer) return;
      await peer.pc.setRemoteDescription({ type: 'answer', sdp });
      this._flushIceBuf(peer);
    });

    this.signal.on('ICE_CANDIDATE', async (msg) => {
      const candidate = msg.payload as RTCIceCandidateInit;
      const peer = this.peers.get(msg.from!);
      if (!peer) return;
      if (peer.pc.remoteDescription) {
        await peer.pc.addIceCandidate(candidate).catch(() => {});
      } else {
        peer.iceBuf.push(candidate);
      }
    });

    this.signal.on('FILE_MANIFEST', (msg) => {
      const m = msg.payload as { transferId: string; name: string; size: number; mimeType: string; sha256: string };
      logDebug('webrtc', 'manifest.in', {
        transferId: m.transferId,
        fromPeerId: msg.from,
        fileName: m.name,
        size: m.size,
      });
      this.receiving.set(m.transferId, {
        fromPeerId: msg.from!, transferId: m.transferId,
        name: m.name, mimeType: m.mimeType, sha256: m.sha256,
        totalBytes: m.size, chunks: [], received: 0,
        startTs: Date.now(), lastProgressTs: Date.now(),
      });
      this.onIncoming({ transferId: m.transferId, fromPeerId: msg.from!, name: m.name, size: m.size, mimeType: m.mimeType, sha256: m.sha256 });
    });

    this.signal.on('TRANSFER_ACCEPT', async (msg) => {
      const { transferId } = msg.payload as { transferId: string };
      logDebug('webrtc', 'transfer.accept', { transferId, from: msg.from });
      const state = this.sending.get(transferId);
      if (!state) return;

      const peer = this._getOrCreatePeer(state.peerId);
      const dc   = peer.pc.createDataChannel(`transfer:${transferId}`, { ordered: true });
      state.dc   = dc;
      this._setupSenderDC(dc, transferId);

      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      this.signal.send({ type: 'SDP_OFFER', to: state.peerId, payload: { sdp: offer.sdp!, transferId } });
    });

    this.signal.on('TRANSFER_DECLINE', (msg) => {
      const { transferId } = msg.payload as { transferId: string };
      logDebug('webrtc', 'transfer.decline', { transferId, from: msg.from }, 'warn');
      if (this.sending.has(transferId)) {
        this.sending.delete(transferId);
        this.onError(transferId, 'DECLINED');
      }
    });

    this.signal.on('TRANSFER_CANCEL', (msg) => {
      const { transferId } = msg.payload as { transferId: string };
      this.sending.delete(transferId);
      this.receiving.delete(transferId);
    });

    this.signal.on('TRANSFER_FAILED', (msg) => {
      const { transferId, code } = msg.payload as { transferId: string; code?: string };
      logDebug('webrtc', 'transfer.failed', { transferId, code, from: msg.from }, 'warn');
      if (this.sending.has(transferId)) {
        this.sending.delete(transferId);
        this.onError(transferId, code ?? 'TRANSFER_FAILED');
      }
    });

    // Receiver: sender has finished pumping and sends the real SHA-256.
    // Update our RecvState so the DC-close verifier uses the real hash.
    this.signal.on('TRANSFER_COMPLETE', (msg) => {
      const { transferId, sha256 } = msg.payload as { transferId: string; sha256: string };
      const r = this.receiving.get(transferId);
      if (r && sha256 && sha256 !== '') {
        r.sha256 = sha256;  // real hash from sender — used when DC closes
      }
    });

    // B3: RESUME — destroy broken peer before creating fresh one
    this.signal.on('RESUME', async (msg) => {
      const { transferId, lastAck } = msg.payload as { transferId: string; lastAck: number };
      const state = this.sending.get(transferId);
      if (!state) return;

      state.bytesSent = lastAck;
      state.lastBytes = lastAck;
      state.paused    = false;
      state.pumping   = false; // B1: reset pumping flag on resume

      if (!state.dc || state.dc.readyState !== 'open') {
        // B3: destroy the old broken peer first so _getOrCreatePeer makes a fresh one
        this.destroyPeer(state.peerId);

        const peer = this._getOrCreatePeer(state.peerId);
        const dc   = peer.pc.createDataChannel(`transfer:${transferId}`, { ordered: true });
        state.dc   = dc;
        this._setupSenderDC(dc, transferId);

        const offer = await peer.pc.createOffer();
        await peer.pc.setLocalDescription(offer);
        this.signal.send({ type: 'SDP_OFFER', to: state.peerId, payload: { sdp: offer.sdp!, transferId } });
      } else {
        this._pumpChunks(transferId);
      }
    });
  }

  // ── Peer management ─────────────────────────────────────────────────────────

  private _getOrCreatePeer(peerId: string): PeerState {
    if (this.peers.has(peerId)) return this.peers.get(peerId)!;

    // B2: read ICE servers NOW (not at construction time) so TURN creds are fresh
    const pc    = new RTCPeerConnection({ iceServers: this.getIceServers() });
    const state: PeerState = { peerId, pc, iceBuf: [] };
    this.peers.set(peerId, state);

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) this.signal.send({ type: 'ICE_CANDIDATE', to: peerId, payload: candidate.toJSON() });
    };

    pc.ondatachannel = (ev) => {
      const dc = ev.channel;
      if (dc.label.startsWith('transfer:')) {
        this._setupReceiverDC(dc, dc.label.slice('transfer:'.length));
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        this.peers.delete(peerId);
      }
    };

    return state;
  }

  private _flushIceBuf(peer: PeerState): void {
    peer.iceBuf.forEach(c => peer.pc.addIceCandidate(c).catch(() => {}));
    peer.iceBuf = [];
  }

  // ── Sender DataChannel ──────────────────────────────────────────────────────

  private _setupSenderDC(dc: RTCDataChannel, transferId: string): void {
    dc.binaryType = 'arraybuffer';
    dc.bufferedAmountLowThreshold = LOW_WATERMARK;

    dc.onopen = () => {
      const s = this.sending.get(transferId);
      if (s) {
        logDebug('webrtc', 'datachannel.open', { transferId, peerId: s.peerId });
        s.startTs = Date.now();
        s.lastTs = Date.now();
        this._pumpChunks(transferId);
      }
    };

    // B1: only call _pumpChunks if not already pumping
    dc.onbufferedamountlow = () => {
      const s = this.sending.get(transferId);
      if (s && !s.paused && !s.cancelled && !s.pumping) this._pumpChunks(transferId);
    };

    dc.onerror = (ev) => { console.error('[WebRTC] Sender DC error', ev); this.onError(transferId, 'DATA_CHANNEL_ERROR'); };
  }

  private async _pumpChunks(transferId: string): Promise<void> {
    const s = this.sending.get(transferId);
    if (!s || !s.dc || s.paused || s.cancelled) return;

    // B1: mutex — only one pump loop at a time per transfer
    if (s.pumping) return;
    s.pumping = true;

    const dc   = s.dc;
    const file = s.file;
    let offset = s.bytesSent;

    try {
      while (offset < file.size) {
        // B10: check dc is still open before every send
        if (dc.readyState !== 'open') break;
        if (dc.bufferedAmount > HIGH_WATERMARK) break;

        const slice  = file.slice(offset, offset + CHUNK_SIZE);
        const buffer = await slice.arrayBuffer();

        // Re-check after async gap
        if (s.cancelled || dc.readyState !== 'open') break;

        dc.send(buffer);
        offset      += buffer.byteLength;
        s.bytesSent  = offset;

        const now     = Date.now();
        const elapsed = (now - s.lastTs) / 1000;
        if (elapsed >= 0.25 || offset >= file.size) {
          const speed = (offset - s.lastBytes) / Math.max(elapsed, 0.001);
          const eta   = speed > 0 ? (file.size - offset) / speed : 0;
          s.lastTs    = now;
          s.lastBytes = offset;

          this.onProgress({ transferId, role: 'sender', bytesDone: offset, totalBytes: file.size, speedBps: speed, etaSec: eta, pct: (offset / file.size) * 100 });
          this.signal.send({ type: 'PROGRESS', to: s.peerId, payload: { transferId, sent: offset, total: file.size, speedBps: speed, etaSec: eta } });
        }

        if (s.paused || s.cancelled) break;
      }
    } finally {
      // B1: always release lock
      s.pumping = false;
    }

    // All bytes sent — await SHA-256 (likely already resolved by now) then complete
    if (offset >= file.size && !s.cancelled) {
      dc.close();
      // Await the concurrent SHA-256 computation (started in sendFile)
      const sha256 = await s.sha256Promise;
      this.sending.delete(transferId);
      logDebug('webrtc', 'transfer.complete.sender', {
        transferId,
        peerId: s.peerId,
        size: file.size,
        durationMs: Date.now() - s.startTs,
      });
      this.signal.send({
        type:    'TRANSFER_COMPLETE',
        payload: {
          transferId,
          sha256,       // real hash — receiver uses this for post-transfer verification
          durationMs: Date.now() - s.startTs,
          sizeBytes:  file.size,
        },
      });
      this.onComplete(transferId, 'sender');
    }
  }

  // ── Receiver DataChannel ────────────────────────────────────────────────────

  private _setupReceiverDC(dc: RTCDataChannel, transferId: string): void {
    dc.binaryType = 'arraybuffer';
    const r = this.receiving.get(transferId);
    if (!r) return;

    dc.onmessage = (ev) => {
      const chunk = new Uint8Array(ev.data as ArrayBuffer);
      r.chunks.push(chunk);
      r.received += chunk.byteLength;

      const now = Date.now();
      if (now - r.lastProgressTs < 150 && r.received < r.totalBytes) return;
      r.lastProgressTs = now;

      const elapsed = Math.max((now - r.startTs) / 1000, 0.001);
      const speed   = r.received / elapsed;
      const eta     = speed > 0 ? (r.totalBytes - r.received) / speed : 0;

      this.onProgress({ transferId, role: 'receiver', bytesDone: r.received, totalBytes: r.totalBytes, speedBps: speed, etaSec: eta, pct: (r.received / r.totalBytes) * 100 });
    };

    dc.onclose = async () => {
      const snap = this.receiving.get(transferId);
      if (!snap) return;

      if (snap.received < snap.totalBytes * 0.995) {
        logDebug('webrtc', 'transfer.incomplete.receiver', {
          transferId,
          received: snap.received,
          total: snap.totalBytes,
        }, 'warn');
        this.onError(transferId, 'INCOMPLETE');
        return; // keep state for in-session resume
      }

      const hashOk = await verifySHA256(snap.chunks, snap.sha256);
      if (!hashOk) {
        logDebug('webrtc', 'transfer.integrity_fail', { transferId }, 'error');
        this.receiving.delete(transferId);
        this.onError(transferId, 'INTEGRITY_FAIL');
        return;
      }

      this._assembleAndDownload(snap);
      this.receiving.delete(transferId);
      logDebug('webrtc', 'transfer.complete.receiver', {
        transferId,
        fromPeerId: snap.fromPeerId,
        fileName: snap.name,
        size: snap.totalBytes,
      });
      this.onComplete(transferId, 'receiver');
    };

    dc.onerror = (ev) => { console.error('[WebRTC] Receiver DC error', ev); this.onError(transferId, 'DATA_CHANNEL_ERROR'); };
  }

  private _assembleAndDownload(r: RecvState): void {
    const blob = new Blob(r.chunks, { type: r.mimeType || 'application/octet-stream' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = r.name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
  }
}
